import { Box, Flex } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { StandardReportInfo } from 'types/reporting';
import { TypedCombobox } from 'types/utils';
import { MonthHourHeatMapChart } from './MonthHourHeatMapChart';
import { OpenAssetButton } from 'components/utils/GoToAssetButton';
import { useIntl } from 'react-intl';

const ALL_ITEMS_ID = -1;

interface MonthHourNetPositionProps {
    info: StandardReportInfo;
}

export function MonthHourNetPosition({ info }: MonthHourNetPositionProps) {
    const intl = useIntl();
    const [selectedYear, setSelectedYear] = useState<number | undefined>(info.startYear);
    const [selectedItem, setSelectedItem] = useState<number>(ALL_ITEMS_ID);

    const studyId = info.studyId;

    const itemTypeId = info.items.find((item) => item.itemId === selectedItem)?.itemTypeId;

    const yearOptions = useMemo(() => {
        if (info.startYear && info.endYear) {
            return Array.from({ length: info.endYear - info.startYear + 1 }, (_, i) => ({
                value: info.startYear! + i,
                label: String(info.startYear! + i),
            }));
        }
        return [];
    }, [info.startYear, info.endYear]);

    const itemOptions = useMemo(() => {
        return [
            {
                itemId: ALL_ITEMS_ID,
                name: intl.formatMessage({ id: 'reporting_portfolio_energy_revenue_month_hour_all_items' }),
            },
            ...info.items,
        ];
    }, [intl, info.items]);

    return (
        <Flex height={'full'} flexDir={'column'}>
            <Flex gap={2} p={4} alignItems="center">
                <Box width={100}>
                    <TypedCombobox
                        valueKey="value"
                        labelKey="label"
                        options={yearOptions}
                        onChange={(value: number) => setSelectedYear(value)}
                        value={selectedYear}
                        showPlaceholder={false}
                        isControlled={true}
                    />
                </Box>
                <Box width="lg">
                    <TypedCombobox
                        valueKey="itemId"
                        labelKey="name"
                        options={itemOptions}
                        onChange={(value: number) => setSelectedItem(value)}
                        value={selectedItem}
                        showPlaceholder={false}
                        isControlled={true}
                    />
                </Box>
                {itemTypeId !== undefined && <OpenAssetButton itemId={selectedItem} itemTypeId={itemTypeId} />}
            </Flex>
            <MonthHourHeatMapChart info={info} studyId={studyId} itemId={selectedItem} year={String(selectedYear)} />
        </Flex>
    );
}
